import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getResources } from "../services/api";
import Sidebar  from "../components/Sidebar";
import PageHero from "../components/PageHero";

const TYPE_LABELS = {
  video:  { label: "Video",        icon: "🎥", color: "#c0392b", bg: "#fde8e8" },
  slides: { label: "Diapositivas", icon: "📊", color: "#2980b9", bg: "#eaf3fb" },
  pdf:    { label: "PDF",          icon: "📄", color: "#a07800", bg: "#fdf3dc" },
  link:   { label: "Link",         icon: "🔗", color: "#16a085", bg: "#e6f5f0" },
};

const FILTERS = ["todos", "video", "slides", "pdf", "link"];

function ResourceCard({ resource, onClick }) {
  const meta = TYPE_LABELS[resource.type] || TYPE_LABELS.link;

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={{
        background: "var(--white)",
        border: "0.5px solid var(--border)",
        borderLeft: `4px solid ${meta.color}`,
        borderRadius: "var(--card-radius)",
        padding: "1rem 1.2rem",
        cursor: "pointer",
        transition: "box-shadow .15s, transform .15s",
        display: "flex", gap: "1rem", alignItems: "flex-start",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,.08)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.transform = "";
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 8, flexShrink: 0,
        background: resource.thumbnail_url ? "transparent" : meta.bg,
        overflow: "hidden", display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: "1.6rem",
      }}>
        {resource.thumbnail_url
          ? <img src={resource.thumbnail_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : meta.icon
        }
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".3rem", flexWrap: "wrap" }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.88rem", color: "var(--charcoal)" }}>
            {resource.title}
          </span>
          <span style={{
            fontSize: "0.65rem", fontFamily: "var(--font-display)", fontWeight: 700,
            background: meta.bg, color: meta.color,
            padding: "2px 8px", borderRadius: 20, letterSpacing: .3, textTransform: "uppercase",
          }}>
            {meta.label}
          </span>
        </div>
        {resource.description && (
          <p style={{
            fontSize: "0.78rem", color: "var(--text-muted)", lineHeight: 1.55,
            overflow: "hidden", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {resource.description}
          </p>
        )}
      </div>

      <span style={{ color: "var(--teal)", fontSize: "1rem", alignSelf: "center", flexShrink: 0 }}>→</span>
    </div>
  );
}

export default function Resources() {
  const navigate                  = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [filter,    setFilter]    = useState("todos");

  useEffect(() => {
    setLoading(true);
    getResources()
      .then(setResources)
      .catch(() => setError("No se pudieron cargar los recursos."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "todos"
    ? resources
    : resources.filter((r) => r.type === filter);

  return (
    <div className="page">
      <PageHero
        section="recursos"
        title="Recursos"
        backBtn={{ label: "Inicio", onClick: () => navigate("/") }}
      />

      <div className="two-col">
        <main>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", marginBottom: "1.2rem" }}>
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  fontFamily: "var(--font-display)", fontWeight: 700,
                  fontSize: "0.72rem", letterSpacing: .5, textTransform: "uppercase",
                  padding: "5px 14px", borderRadius: 20, border: "1.5px solid",
                  borderColor: filter === f ? "var(--teal)" : "var(--border)",
                  background:  filter === f ? "var(--teal)" : "var(--white)",
                  color:       filter === f ? "#fff" : "var(--text-muted)",
                  cursor: "pointer", transition: "all .15s",
                }}
              >
                {f === "todos" ? "Todos" : TYPE_LABELS[f]?.label}
              </button>
            ))}
          </div>

          {error && <p style={{ color: "#c0392b" }}>{error}</p>}

          {loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: ".8rem" }}>
              {[1, 2, 3, 4].map((n) => (
                <div className="skel" key={n} style={{ display: "flex", gap: "1rem", padding: "1rem" }}>
                  <div className="skel-line" style={{ width: 52, height: 52, borderRadius: 8, flexShrink: 0 }} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                    <div className="skel-line" style={{ width: "55%", height: 13 }} />
                    <div className="skel-line" style={{ width: "90%", height: 11 }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="card">
              <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>
                {filter === "todos"
                  ? "No hay recursos publicados aún."
                  : `No hay recursos de tipo "${TYPE_LABELS[filter]?.label}" aún.`}
              </p>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: ".8rem" }}>
            {!loading && filtered.map((r) => (
              <ResourceCard
                key={r.id}
                resource={r}
                onClick={() => navigate(`/recursos/${r.id}`)}
              />
            ))}
          </div>
        </main>

        <Sidebar />
      </div>
    </div>
  );
}